import { BookmarkPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { contentTypeLabel, type TemplateKind } from "@/lib/composerTemplates";

type Props = {
  search: string;
  setSearch: (v: string) => void;
  onSaveCurrent?: () => void;
  canSaveCurrent?: boolean;
  tabContentTypes: string[];
  contentTypeFilter: string | null;
  setContentTypeFilter: (v: string | null | ((cur: string | null) => string | null)) => void;
  kindFilter: TemplateKind | "all";
  setKindFilter: (k: TemplateKind | "all") => void;
};

export const TemplatesFilters = ({
  search, setSearch, onSaveCurrent, canSaveCurrent,
  tabContentTypes, contentTypeFilter, setContentTypeFilter,
  kindFilter, setKindFilter,
}: Props) => (
  <>
    <div className="flex flex-wrap items-center gap-2">
      <Input
        placeholder="Search templates…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="h-8 max-w-xs"
      />
      {onSaveCurrent && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="outline" onClick={onSaveCurrent} disabled={!canSaveCurrent} className="ml-auto">
              <BookmarkPlus className="h-4 w-4" />
              Save current Draft as template
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            Saves the structure of the Draft Canvas you have open as a reusable template.
          </TooltipContent>
        </Tooltip>
      )}
    </div>

    {tabContentTypes.length > 1 && (
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Type:</span>
        <Button
          type="button"
          size="sm"
          variant={contentTypeFilter === null ? "secondary" : "ghost"}
          className="h-6 px-2 text-[11px]"
          onClick={() => setContentTypeFilter(null)}
        >
          All
        </Button>
        {tabContentTypes.map((ct) => (
          <Button
            key={ct}
            type="button"
            size="sm"
            variant={contentTypeFilter === ct ? "secondary" : "ghost"}
            className="h-6 px-2 text-[11px]"
            onClick={() => setContentTypeFilter((cur) => (cur === ct ? null : ct))}
          >
            {contentTypeLabel(ct)}
          </Button>
        ))}
      </div>
    )}
  </>
);

export const KindFilterChips = ({
  kindFilter, setKindFilter,
}: { kindFilter: TemplateKind | "all"; setKindFilter: (k: TemplateKind | "all") => void }) => (
  <div className="mt-2 flex flex-wrap items-center gap-1.5">
    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Kind:</span>
    {(["all", "static", "dynamic"] as const).map((k) => (
      <Tooltip key={k}>
        <TooltipTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant={kindFilter === k ? "secondary" : "ghost"}
            className="h-6 px-2 text-[11px] capitalize"
            onClick={() => setKindFilter(k)}
          >
            {k}
          </Button>
        </TooltipTrigger>
        {k !== "all" && (
          <TooltipContent className="max-w-xs">
            {k === "static"
              ? "Skeleton-only — selecting drops the structure straight into the Draft Canvas. No AI, no questions."
              : "Intake-driven — selecting opens a short wizard, then the Planning Agent in chat with your answers pre-loaded."}
          </TooltipContent>
        )}
      </Tooltip>
    ))}
  </div>
);
